import { Box, Typography } from "@mui/material";
import React, { useEffect, useRef, useCallback } from "react";
import { useSnackbar } from "notistack";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router-dom";
import CustomizedBreadcrumbs from "../../../components/Custom/CustomizedBreadcrumbs";
import CustomizedTab from "../../../components/Custom/CustomizedTab";
import PurchaseReturnTable from "../../../components/Table/DocumentTable/PurchaseReturnTable";
import { getAllPurchaseReturns } from "../../../features/Purchase/PurchaseReturn/purchase-return-actions";
import { purchaseReturnActions } from "../../../features/Purchase/PurchaseReturn/purchase-return-slice";
import { filterDateWithUnix } from "../../../utils/filterparams";

const PurchaseReturn = ({ report }) => {
  const gridRef = useRef();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const isFilter = searchParams.get("filter");

  const breadcrumbs = [
    {
      name: t("purchase.index"),
      to: "/purchase",
    },
    {
      name: t("purchase.return.index"),
    },
  ];

  report &&
    breadcrumbs.splice(1, 0, {
      name: t("purchase.report.index"),
      to: "/purchase/report",
    });

  const currentTab = pathname + (isFilter ? `?filter=${isFilter}` : "");

  const tabs = [
    {
      label: t("inventory.all"),
      path: `${pathname}`,
    },
    {
      label: t("status.draft"),
      path: `${pathname}?filter=draft`,
    },
    {
      label: t("status.waitApprove"),
      path: `${pathname}?filter=waitApprove`,
    },
    {
      label: t("status.notApproved"),
      path: `${pathname}?filter=notApproved`,
    },
    {
      label: t("status.approved"),
      path: `${pathname}?filter=approved`,
    },
    {
      label: t("status.finished"),
      path: `${pathname}?filter=finished`,
    },
    {
      label: t("status.cancelled"),
      path: `${pathname}?filter=cancelled`,
    },
  ];

  const onFilterChanged = useCallback(
    (params) => {
      const instance = params.api.getFilterInstance("render_status");
      switch (isFilter) {
        case "draft":
          instance?.setModel({ values: ["draft"] });
          break;
        case "waitApprove":
          instance?.setModel({ values: ["PENDING"] });
          break;
        case "notApproved":
          instance?.setModel({ values: ["DECLINED"] });
          break;
        case "approved":
          instance?.setModel({ values: ["approved"] });
          break;
        case "finished":
          instance?.setModel({ values: ["finished"] });
          break;
        case "cancelled":
          instance?.setModel({ values: ["cancelled"] });
          break;
        default:
          instance?.setModel({
            values: ["draft", "PENDING", "DECLINED", "approved", "finished"],
          });
          break;
      }
      params.api.onFilterChanged();
    },
    [isFilter]
  );

  const datasource = {
    getRows(params) {
      const request = params.request;
      const { filterModel } = request;
      const {
        document_id,
        contact_document_id,
        contact_name,
        issue_date,
        delivery_date,
        created_by,
        employee_list,
        ...otherFilterModel
      } = filterModel;

      const formatFilterModel = {
        ...otherFilterModel,
        document_id: document_id && {
          ...document_id,
          mode: "insensitive",
        },
        contact_document_id: contact_document_id && {
          ...contact_document_id,
          mode: "insensitive",
        },
        contact: contact_name && {
          filterType: "object",
          filter: {
            first_name: {
              ...contact_name,
              mode: "insensitive",
            },
          },
        },
        issue_date: issue_date && filterDateWithUnix(issue_date),
        delivery_date: delivery_date && filterDateWithUnix(delivery_date),
        employee_list: employee_list && {
          filterType: "objectArray",
          type: "some",
          filter: {
            first_name: { ...employee_list, mode: "insensitive" },
          },
        },
        created_by: created_by && {
          filterType: "object",
          filter: {
            first_name: {
              ...created_by,
              mode: "insensitive",
            },
          },
        },
      };
      dispatch(
        getAllPurchaseReturns(
          {
            startRow: request.startRow,
            endRow: request.endRow,
            filterModel: formatFilterModel,
            sortModel: request.sortModel,
          },
          params,
          enqueueSnackbar
        )
      );
    },
  };

  const onGridReady = (params) => {
    onFilterChanged(params);
    params.api.setServerSideDatasource(datasource);
  };

  useEffect(() => {
    if (gridRef.current.api) {
      onFilterChanged(gridRef.current);
    }
    return () => {
      dispatch(purchaseReturnActions.resetPurchaseReturn());
    };
  }, [dispatch, onFilterChanged]);

  return (
    <>
      <CustomizedBreadcrumbs breadcrumbs={breadcrumbs} />
      <Box sx={{ my: 3, display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h5">{t("purchase.return.index")}</Typography>
      </Box>
      {!report && (
        <CustomizedTab table tabs={tabs} currentTab={currentTab} divider />
      )}
      <PurchaseReturnTable gridRef={gridRef} onGridReady={onGridReady} />
    </>
  );
};

export default PurchaseReturn;
